import {
  CloudConvertService,
  // requests
  CloudConvertServiceOptions,
  GetWorkflowDocumentPreviewOptions,
  PostHtmlToPdfOptions,
  GetDocVaultDocumentsOptions,
  PostDocVaultDocumentsOptions,
  DeleteDocVaultDocumentsOptions,
  GetDefaultDocVaultDocumentOptions,
  GetAllWorkFlowSummaryOptions,
  GetWorkflowOptions,
  PostWorkflowOptions,
  DeleteWorkflowOptions,
  PreviewWorkflowOptions,
  ScanAndUploadDocOptions,
  // responses
  PostHtmlToPdfResponse,
  DocVaultDocumentResponse,
  WorkFlowSummaryResponse,
  WorkflowPreviewResponse,
  GetDocumentPreviewOptions,
  ScanAndUploadDocResponse,
} from '@digital-advice/ms-cloud-convert-sdk';
import {apiKey, getLogger, endpoint, interceptors, sourceInfo} from '@advisor/services/sdk';
import Service from '@advisor/util/Service';

const {MS_BASE} = Service;

export const defaultOptions: CloudConvertServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint,
  interceptors,
  sourceInfo,
};

export default class CloudConvertServices {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static get API_BASE_URL() {
    return `${MS_BASE}/v1/convert`;
  }

  static async sdkService(options: CloudConvertServiceOptions = defaultOptions): Promise<CloudConvertService> {
    const service = await new CloudConvertService(options);

    return service;
  }

  /**
   * Gateway endpoint: getWorkflowDocumentPreview
   * @param payload - A {@link GetWorkflowDocumentPreviewOptions} object.
   */
  static async getWorkflowDocumentPreview(payload: GetWorkflowDocumentPreviewOptions): Promise<string[]> {
    const sdkService = await this.sdkService();

    return sdkService.getWorkflowDocumentPreview(payload);
  }

  /**
   * Gateway endpoint: postHtmlToPdf
   * @param payload - A {@link GetWorkflowDocumentPreviewOptions} object.
   */
  static async postHtmlToPdf(payload: PostHtmlToPdfOptions): Promise<PostHtmlToPdfResponse> {
    const sdkService = await this.sdkService();

    return sdkService.postHtmlToPdf(payload);
  }

  /**
   * Gateway endpoint: getDocVaultDocuments
   * @param payload - A {@link GetDocVaultDocumentsOptions} object.
   */
  static async getDocVaultDocuments(payload: GetDocVaultDocumentsOptions): Promise<DocVaultDocumentResponse[]> {
    const sdkService = await this.sdkService();

    return sdkService.getDocVaultDocuments(payload);
  }

  /**
   * Gateway endpoint: getDocVaultDocument
   * @param payload - A {@link GetDocVaultDocumentsOptions} object.
   */
  static async getDocVaultDocument(payload: GetDocVaultDocumentsOptions): Promise<DocVaultDocumentResponse> {
    const sdkService = await this.sdkService();

    return sdkService.getDocVaultDocument(payload);
  }

  /**
   * Gateway endpoint: createDocVaultDocument
   * @param payload - A {@link PostDocVaultDocumentsOptions} object.
   */
  static async createDocVaultDocument(payload: PostDocVaultDocumentsOptions): Promise<DocVaultDocumentResponse> {
    const sdkService = await this.sdkService();

    return sdkService.createDocVaultDocument(payload);
  }

  /**
   * Gateway endpoint: updateDocVaultDocument
   * @param payload - A {@link PostDocVaultDocumentsOptions} object.
   */
  static async updateDocVaultDocument(payload: PostDocVaultDocumentsOptions): Promise<DocVaultDocumentResponse> {
    const sdkService = await this.sdkService();

    return sdkService.updateDocVaultDocument(payload);
  }

  /**
   * Gateway endpoint: deleteDocVaultDocument
   * @param payload - A {@link DeleteDocVaultDocumentsOptions} object.
   */
  static async deleteDocVaultDocument(payload: DeleteDocVaultDocumentsOptions): Promise<void> {
    const sdkService = await this.sdkService();

    return sdkService.deleteDocVaultDocument(payload);
  }

  /**
   * Gateway endpoint: getExampleDocument
   * @param payload - A {@link GetDefaultDocVaultDocumentOptions} object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async getExampleDocument(payload: GetDefaultDocVaultDocumentOptions): Promise<any> {
    const sdkService = await this.sdkService();

    return sdkService.getExampleDocument(payload);
  }

  /**
   * Gateway endpoint: getAllWorkFlowSummary
   * @param payload - A {@link GetAllWorkFlowSummaryOptions} object.
   */
  static async getAllWorkFlowSummary(payload: GetAllWorkFlowSummaryOptions): Promise<WorkFlowSummaryResponse[]> {
    const sdkService = await this.sdkService();

    return sdkService.getAllWorkFlowSummary(payload);
  }

  /**
   * Gateway endpoint: getWorkflow
   * @param payload - A {@link GetWorkflowOptions} object.
   */
  static async getWorkflow(payload: GetWorkflowOptions): Promise<WorkFlowSummaryResponse> {
    const sdkService = await this.sdkService();

    return sdkService.getWorkflow(payload);
  }

  /**
   * Gateway endpoint: createWorkflow
   * @param payload - A {@link GetWorkflowDocumentPreviewOptions} object.
   */
  static async createWorkflow(payload: PostWorkflowOptions): Promise<WorkFlowSummaryResponse> {
    const sdkService = await this.sdkService();

    return sdkService.createWorkflow(payload);
  }

  /**
   * Gateway endpoint: updateWorkflow
   * @param payload - A {@link GetWorkflowDocumentPreviewOptions} object.
   */
  static async updateWorkflow(payload: PostWorkflowOptions): Promise<WorkFlowSummaryResponse> {
    const sdkService = await this.sdkService();

    return sdkService.updateWorkflow(payload);
  }

  /**
   * Gateway endpoint: deleteWorkflow
   * @param payload - A {@link DeleteWorkflowOptions} object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async deleteWorkflow(payload: DeleteWorkflowOptions): Promise<any> {
    const sdkService = await this.sdkService();

    return sdkService.deleteWorkflow(payload);
  }

  /**
   * Gateway endpoint: previewWorkflow
   * @param payload - A {@link PreviewWorkflowOptions} object.
   */
  static async previewWorkflow(payload: PreviewWorkflowOptions): Promise<WorkflowPreviewResponse> {
    const sdkService = await this.sdkService();

    return sdkService.previewWorkflow(payload);
  }

  /**
   * Gateway endpoint: getDocumentPreviewByDocId
   * @param {Object} A {@link GetDocumentPreviewOptions} object.
   */

  static async getDocumentPreviewByDocId(payload: GetDocumentPreviewOptions): Promise<WorkflowPreviewResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getDocumentPreviewByDocId(payload);
  }

  /**
   * Gateway endpoint: scanAndUploadDocument
   * @param payload - A {@link ScanAndUploadDocOptions} object.
   */
  static async scanAndUploadDocument(payload: ScanAndUploadDocOptions): Promise<ScanAndUploadDocResponse> {
    const sdkService = await this.sdkService();
    const resp = await sdkService.scanAndUploadDocument(payload);

    return resp;
  }
}
